import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { AlertLogResponse } from '@models/index';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { getOne, StateDictionary } from '../../helpers';
import { createCustomStateSetOnUrlHandler, createStateResetHandler } from '../../helpers/state-reset-handler';
import { AlertLogsService } from './alert-logs.service';

interface AlertLogsState {
  selectedLog: AlertLogResponse | null;
  isLoading: boolean;
  isSelectedLogLoading: boolean;
}

const initialState: AlertLogsState = {
  selectedLog: null,
  isLoading: false,
  isSelectedLogLoading: false,
};

const alertLogsDictionary: StateDictionary<AlertLogResponse> = {
  entityList: '',
  listLoading: 'isLoading',
  selectedEntity: 'selectedLog',
  selectedEntityLoading: 'isSelectedLogLoading',
  entityId: 'id',
  createLoading: '',
};

export const AlertLogsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('alert-logs-store'),

  withComputed(state => ({
    selectedLogId: computed(() => state.selectedLog()?.id || null),
  })),

  withMethods((store, alertLogsService = inject(AlertLogsService), router = inject(Router)) => {
    createStateResetHandler<AlertLogsState>('/monitoring/logs')(store, initialState);
    createCustomStateSetOnUrlHandler<AlertLogsState>('/monitoring/logs', { selectedLog: null })(store);

    const repositoryMethods = {
      getAlertLogById: getOne<AlertLogResponse>({
        store,
        service: alertLogsService,
        dictionary: alertLogsDictionary,
      }),
    };

    const navigationMethods = {
      goToLogActions: async (id: string): Promise<boolean> => (await router.navigate(['monitoring', 'logs', id])) ?? false,
    };

    return { ...repositoryMethods, ...navigationMethods };
  }),
);

export interface AlertLogsStoreType {
  readonly selectedLog: Signal<AlertLogResponse | null>;
  readonly isLoading: Signal<boolean>;
  readonly isSelectedLogLoading: Signal<boolean>;

  readonly selectedLogId: Signal<string | null>;

  readonly getAlertLogById: (id: string) => void;
  readonly goToLogActions: (id: string) => Promise<boolean>;
}
